import {
  ExceptionOutlined,
  PrinterOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import {
  Button,
  Col,
  DatePicker,
  Form,
  Row,
  Select,
  Table,
  Typography,
} from "antd";
import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router";
import { useReactToPrint } from "react-to-print";
import { Fixed2, rangePresets } from "../../../../common/utils/common.utils";
import { SelectVendors } from "../../../../components/common/FormItem/SelectCustomFeilds";
import CommonViewReport from "../../../../components/common/Invoice/ViewInvoiceInfo/CommonViewReport";
import BreadCrumb from "../../../../components/common/breadCrumb/BreadCrumb";
import { loadingIndicator } from "../../../../components/common/spinner/LoadingIndicator";
import FormHeaderTitle from "../../../Invoice(Visa)/Components/FormHeaderTitle";
import { useGetVendorPaymentReportQuery } from "../endpoints/VendorPaymentReportEndpoints";
import { IVendorPaymentPost } from "../types/VendorPaymentTypes";
import { VendorPaymentReportUtils } from "../utils/VendorPaymentReportUtils";
import DownloadExcelReport from "../../../../components/common/ExcelReport";

const VendorPaymentReport = () => {
  const toDate = dayjs().format("YYYY-MM-DD");
  const [date, setDate] = useState({ toDate: toDate, fromDate: toDate });
  const [vendor, setVendor] = useState("");
  const [paymentType, setPaymentType] = useState("");
  const [form] = Form.useForm();
  const location = useLocation();

  const { data, isLoading, isFetching } = useGetVendorPaymentReportQuery(
    {
      paymentType,
      formDate: date.fromDate,
      todDate: date.toDate,
      vendor: vendor,
    },
    { skip: !vendor }
  );

  // This useEffect is working based on Params
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const dailyValue = searchParams.get("daily");

    const monthFormDate = searchParams.get("from_date");
    const monthToDate = searchParams.get("to_date");

    const yearFormDate = searchParams.get("y_from_date");
    const yearToDate = searchParams.get("y_to_date");

    if (dailyValue) {
      onFinish({
        date_range: [dailyValue, dailyValue],
        invoice_combvendor_id: "all",
        paymentType: "",
      });
    }

    if (monthFormDate && monthToDate) {
      form.setFieldValue("date_range", [
        dayjs(monthFormDate),
        dayjs(monthToDate),
      ]);
      onFinish({
        date_range: [monthFormDate, monthToDate],
        invoice_combvendor_id: "all",
        paymentType: "",
      });
    }

    if (yearFormDate && yearToDate) {
      form.setFieldValue("date_range", [
        dayjs(yearFormDate),
        dayjs(yearToDate),
      ]);
      onFinish({
        date_range: [yearFormDate, yearToDate],
        invoice_combvendor_id: "all",
        paymentType: "",
      });
    }
  }, [location.search]);

  const onFinish = async (values: IVendorPaymentPost) => {
    const setFrom_date = dayjs(values.date_range[0]).format("YYYY-MM-DD");
    const setTo_date = dayjs(values.date_range[1]).format("YYYY-MM-DD");

    setVendor(values.invoice_combvendor_id);
    setDate({ fromDate: setFrom_date, toDate: setTo_date });
    setPaymentType(values.paymentType || "");
  };

  const componentRef = useRef(null);
  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    documentTitle: `Vendor_Payment_Report`,
  });

  const common_style: React.CSSProperties = {
    textAlign: "right",
    marginRight: "10px",
    fontWeight: "bold",
  };

  // @HANDLE CONTENTS
  const jsx_com = (
    <div>
      <FormHeaderTitle title="Vendor Payment Report" />

      <Table
        size="small"
        bordered
        dataSource={data?.data}
        columns={VendorPaymentReportUtils()}
        loading={{
          spinning: isLoading || isFetching,
          indicator: loadingIndicator,
        }}
        pagination={false}
        summary={(pageData) => {
          let totalCharge = 0;
          let vendorTotal = 0;

          pageData.forEach(({ online_charge, payment_amount }) => {
            totalCharge += Number(online_charge);
            vendorTotal += Number(payment_amount);
          });
          return (
            <>
              <Table.Summary.Row>
                <Table.Summary.Cell index={3} colSpan={2} />
                <Table.Summary.Cell index={3} colSpan={3}>
                  <Typography.Text strong>Total</Typography.Text>
                </Table.Summary.Cell>

                <Table.Summary.Cell index={7}>
                  <Typography style={common_style}>
                    {Fixed2(totalCharge || 0)}
                  </Typography>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={7}>
                  <Typography style={common_style}>
                    {Fixed2(vendorTotal || 0)}
                  </Typography>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={3} colSpan={1} />
              </Table.Summary.Row>
            </>
          );
        }}
      />
    </div>
  );

  // @HANDLE PRINT WITH HEADER FOOTER
  const print_content = (
    <div hidden>
      <CommonViewReport
        children={jsx_com}
        printRef={componentRef}
        title={{ info_title: "", title: "Vendor Payment Report" }}
        extraInfo={{
          date:
            `${dayjs(date.fromDate).format("DD-MM-YYYY")} to ${dayjs(
              date.toDate
            ).format("DD-MM-YYYY")}` || "",
        }}
      />
    </div>
  );

  return (
    <>
      <div>
        <BreadCrumb arrOfOption={["Report", "Vendor Payment Report"]} />

        <Form
          layout="vertical"
          form={form}
          onFinish={onFinish}
          initialValues={{
            invoice_combvendor_id: "all",
            date_range: [dayjs(), dayjs()],
          }}
        >
          <Row justify={"space-between"}>
            <Col sm={24} md={12} lg={12}>
              <div style={{ display: "flex", gap: 10 }}>
                <SelectVendors
                  name="invoice_combvendor_id"
                  size={6}
                  placeholder="Select Vendor"
                  offDropDown
                  showAll
                />

                <Form.Item name="date_range">
                  <DatePicker.RangePicker
                    presets={rangePresets}
                    format={"DD-MM-YYYY"}
                    allowClear={false}
                  />
                </Form.Item>
                <Form.Item name="paymentType">
                  <Select
                    style={{ width: "220px" }}
                    placeholder="Select Type"
                    allowClear
                    options={[
                      {
                        label: "INVOICE",
                        value: "INVOICE",
                      },
                      {
                        label: "VENDOR",
                        value: "VENDOR",
                      },
                      {
                        label: "TICKET",
                        value: "TICKET",
                      },
                      {
                        label: "ADVANCE",
                        value: "ADVANCE",
                      },
                    ]}
                  />
                </Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<SearchOutlined />}
                >
                  Search
                </Button>
              </div>
            </Col>

            <Col style={{ display: "flex", gap: 10 }}>
              <Button type="primary" onClick={handlePrint}>
                <PrinterOutlined />
                Print
              </Button>
              <DownloadExcelReport
                excelName={`vendor_payment_report`}
                description={`VENDOR PAYMENT REPORT`}
                excelTableHead={[
                  "Payment Date",
                  "Voucher No",
                  "Vendor Name",
                  "Payment Type",
                  "Online Charge",
                  "Amount",
                  "Note",
                ]}
                excelData={
                  data?.data?.length
                    ? data.data.map((item) => ({
                        "Payment Date": dayjs(item.payment_date).format(
                          "DD MMM YYYY"
                        ),
                        "Voucher No": item.vouchar_no || "N/A",
                        "Vendor Name": item.vendor_name || "N/A",
                        "Payment Type": item.vpay_payment_to || "N/A",
                        "Online Charge": item.online_charge || "0",
                        Amount: item.payment_amount || "0",
                        Note: item.note || "",
                      }))
                    : []
                }
              />
            </Col>
          </Row>
        </Form>

        {/* //----------------//------------// @LEDGER TABLE HERE //----------------//------------// */}
        {jsx_com}

        {/*-----//---------//--------- @PRINTABLE CONTENT -----------//-----------//-----*/}
        {print_content}
      </div>
    </>
  );
};

export default VendorPaymentReport;
